import { getAuthFailureMessage, type AuthProviderPort, type AuthUser } from "./auth";
import type { HandoffRedeemer } from "./handoff";
import type { OwnerAccessRepository } from "./roles";

export type AppState =
  | { kind: "loading"; message?: string }
  | { kind: "config-error"; message: string }
  | { kind: "handoff-error"; message: string }
  | { kind: "crm-required" }
  | { kind: "denied"; reason: string }
  | { kind: "owner"; user: AuthUser; route: string; mockMode: boolean };

const navItems = [
  ["overview", "Обзор"],
  ["entries", "Операции"],
  ["imports", "Импорт"],
  ["reports", "Отчёты"],
  ["settings", "Настройки"]
] as const;

const placeholders: Record<string, string> = {
  overview: "Панель готова к подключению к Firestore после миграции.",
  entries: "Операции появятся после утвержденной миграции данных.",
  imports: "Импорт будет подключен через серверные функции.",
  reports: "Отчеты будут формироваться сервером и храниться в защищенном Storage.",
  settings: "Настройки будут доступны только владельцу."
};

export function getRoute(hash: string): string {
  const route = hash.replace(/^#\/?/, "") || "overview";
  return navItems.some(([key]) => key === route) ? route : "overview";
}

export function readHandoffCode(location: Location): string | null {
  const url = new URL(location.href);
  const code = url.searchParams.get("code");
  return code && /^[A-Za-z0-9_-]{43,256}$/.test(code) ? code : null;
}

export function removeHandoffCodeFromUrl(history: History, location: Location): void {
  const url = new URL(location.href);
  if (!url.searchParams.has("code")) return;
  url.searchParams.delete("code");
  const clean = `${url.pathname}${url.search}${url.hash}`;
  history.replaceState({}, document.title, clean || "/");
}

export function renderApp(state: AppState): string {
  if (state.kind === "loading") {
    return `<main class="la-center" data-state="loading"><div class="la-spinner"></div><p>${escapeHtml(state.message || "Загрузка доступа...")}</p></main>`;
  }
  if (state.kind === "config-error") {
    return `<main class="la-center" data-state="config-error"><h1>Бухгалтерия</h1><p>${escapeHtml(state.message)}</p></main>`;
  }
  if (state.kind === "handoff-error") {
    return `
      <main class="la-center" data-state="handoff-error">
        <h1>Доступ не подтверждён</h1>
        <p>${escapeHtml(state.message)}</p>
        <div class="la-actions">
          <button class="la-button la-button-secondary" data-action="crm-return">Вернуться в CRM</button>
          <button class="la-button la-button-secondary" data-action="sign-out">Сбросить сессию</button>
        </div>
      </main>`;
  }
  if (state.kind === "crm-required") {
    return `
      <main class="la-center" data-state="crm-required">
        <h1>Бухгалтерия</h1>
        <p>Бухгалтерия открывается через AGC CRM.</p>
        <div class="la-actions">
          <button class="la-button la-button-secondary" data-action="crm-return">Вернуться в CRM</button>
        </div>
      </main>`;
  }
  if (state.kind === "denied") {
    return `
      <main class="la-center" data-state="denied">
        <h1>Доступ запрещён</h1>
        <p>${escapeHtml(state.reason)}</p>
        <div class="la-actions">
          <button class="la-button la-button-secondary" data-action="crm-return">Вернуться в CRM</button>
          <button class="la-button la-button-secondary" data-action="sign-out">Выйти</button>
        </div>
      </main>`;
  }

  const activeRoute = getRoute(state.route);
  const nav = navItems
    .map(([key, label]) => {
      const active = key === activeRoute ? " aria-current=\"page\"" : "";
      return `<a href="#/${key}" class="la-nav-link"${active}>${label}</a>`;
    })
    .join("");

  return `
    <div class="la-app" data-state="owner">
      ${state.mockMode ? '<div class="la-mock-banner">Development mock mode</div>' : ""}
      <aside class="la-sidebar">
        <div>
          <p class="la-kicker">LTD Accounting</p>
          <h1>Бухгалтерия</h1>
        </div>
        <nav class="la-nav">${nav}</nav>
      </aside>
      <main class="la-main">
        <header class="la-topbar">
          <div>
            <p class="la-kicker">Владелец</p>
            <strong>${escapeHtml(state.user.displayName || "Owner")}</strong>
          </div>
          <button class="la-icon-button" title="Выйти" data-action="sign-out">⎋</button>
        </header>
        <section class="la-workspace">
          <h2>${navItems.find(([key]) => key === activeRoute)?.[1] ?? "Обзор"}</h2>
          <p>${placeholders[activeRoute]}</p>
          <div class="la-placeholder-grid">
            <div><span>Статус</span><strong>Скаффолд</strong></div>
            <div><span>Данные</span><strong>Не подключены</strong></div>
            <div><span>Хостинг</span><strong>Cloudflare Pages</strong></div>
          </div>
        </section>
      </main>
    </div>`;
}

export function mountApp(
  root: HTMLElement,
  authProvider: AuthProviderPort,
  ownerAccess: OwnerAccessRepository,
  handoffRedeemer: HandoffRedeemer,
  mockMode: boolean
): () => void {
  root.innerHTML = renderApp({ kind: "loading" });
  let unsubscribe: () => void = () => undefined;
  let disposed = false;
  let currentOwnerUser: AuthUser | null = null;
  const unregisterGlobalErrors = registerGlobalFailureHandlers(root);

  const renderVerifiedUser = (user: AuthUser) => {
    root.innerHTML = renderApp({ kind: "loading", message: "Проверка доступа..." });
    ownerAccess
      .verifyOwner(user)
      .then((result) => {
        if (disposed) return;
        currentOwnerUser = result.allowed ? user : null;
        root.innerHTML = result.allowed
          ? renderApp({ kind: "owner", user, route: window.location.hash, mockMode })
          : renderApp({ kind: "denied", reason: result.reason || "Доступ владельца не подтверждён." });
      })
      .catch(() => {
        if (!disposed) {
          currentOwnerUser = null;
          root.innerHTML = renderApp({ kind: "denied", reason: "Проверка доступа владельца не завершена." });
        }
      });
  };

  authProvider
    .initialize()
    .then(async () => {
      if (disposed) return;
      const code = readHandoffCode(window.location);
      if (code) {
        removeHandoffCodeFromUrl(window.history, window.location);
        root.innerHTML = renderApp({ kind: "loading", message: "Подключение через AGC CRM..." });
        const redemption = await handoffRedeemer.redeem(code);
        await authProvider.signInWithHandoffToken(redemption.firebaseCustomToken);
      }
      if (disposed) return;
      unsubscribe = authProvider.onChange((user) => {
        if (!user) {
          currentOwnerUser = null;
          root.innerHTML = renderApp({ kind: "crm-required" });
          return;
        }
        renderVerifiedUser(user);
      });
    })
    .catch((error) => {
      if (!disposed) {
        root.innerHTML = renderApp({ kind: "handoff-error", message: getAuthFailureMessage(error) });
      }
    });

  const onHashChange = () => {
    const state = root.querySelector("[data-state='owner']");
    if (state && currentOwnerUser) {
      root.innerHTML = renderApp({
        kind: "owner",
        user: currentOwnerUser,
        route: window.location.hash,
        mockMode
      });
    }
  };
  window.addEventListener("hashchange", onHashChange);

  root.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;
    const action = target.dataset.action;
    if (action === "sign-out") void authProvider.signOut();
    if (action === "crm-return") {
      root.innerHTML = renderApp({ kind: "crm-required" });
    }
  });

  return () => {
    disposed = true;
    unregisterGlobalErrors();
    window.removeEventListener("hashchange", onHashChange);
    unsubscribe();
  };
}

export function registerGlobalFailureHandlers(root: HTMLElement): () => void {
  const renderFailure = () => {
    if (!root.innerHTML.trim() || !root.querySelector("[data-state]")) {
      root.innerHTML = renderApp({
        kind: "handoff-error",
        message: "Приложение остановилось из-за ошибки. Вернитесь в AGC CRM и откройте Бухгалтерию заново."
      });
    }
  };
  const onError = () => renderFailure();
  const onUnhandledRejection = () => renderFailure();
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onUnhandledRejection);
  return () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onUnhandledRejection);
  };
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => {
    const replacements: Record<string, string> = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      "\"": "&quot;",
      "'": "&#039;"
    };
    return replacements[char];
  });
}
